import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Slack, FileCode, GitBranch, Workflow } from 'lucide-react';
import { cn } from '../lib/utils';

const SOURCE_TYPE_CONFIG = {
  slack: { name: 'Slack', icon: Slack, color: 'bg-[#4A154B]/10', textColor: 'text-[#4A154B] dark:text-[#E01E5A]' },
  linear: { name: 'Linear', icon: Workflow, color: 'bg-[#5E6AD2]/10', textColor: 'text-[#5E6AD2]' },
  jira: { name: 'Jira', icon: FileCode, color: 'bg-[#0052CC]/10', textColor: 'text-[#0052CC]' },
  github: { name: 'GitHub', icon: GitBranch, color: 'bg-neutral-100 dark:bg-neutral-800', textColor: 'text-neutral-900 dark:text-neutral-100' },
  'google-drive': { name: 'Google Drive', icon: FileCode, color: 'bg-[#1FA463]/10', textColor: 'text-[#1FA463]' },
};

export { SOURCE_TYPE_CONFIG };

export default function SourceTypeGrid({ sourcesByType, onSelectType, loading }) {
  const allTypes = Object.keys(SOURCE_TYPE_CONFIG);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {allTypes.map((typeId) => (
          <Card key={typeId} className="animate-pulse">
            <div className="p-6 flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-muted" />
              <div className="space-y-2 flex-1">
                <div className="h-5 w-24 bg-muted rounded" />
                <div className="h-4 w-16 bg-muted rounded" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {allTypes.map((typeId, index) => {
        const config = SOURCE_TYPE_CONFIG[typeId];
        const instances = sourcesByType[typeId] || [];
        const Icon = config.icon;
        const count = instances.length;

        return (
          <Card
            key={typeId}
            className="hover:shadow-glass-lg hover:scale-[1.01] transition-all duration-200 cursor-pointer group animate-fade-up"
            style={{ animationDelay: `${index * 60}ms` }}
            onClick={() => onSelectType(typeId)}
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    'h-14 w-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300',
                    config.color,
                    config.textColor,
                  )}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg mb-1">
                    {config.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {count} {count === 1 ? 'source' : 'sources'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {count > 0 && (
                  <Badge
                    variant="secondary"
                    className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
                  >
                    Connected
                  </Badge>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
